import { Component } from '@theme/component';
import { SectionUpdateEvent, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * Client-side cache of already-fetched section HTML, keyed by the exact request URL, with a TTL.
 * Makes combined-listing variant switches instant (prefetch on idle/hover → serve from memory) and,
 * combined with the concurrency queue below, prevents re-requesting the same section and flooding the
 * origin (429). Shared across all pickers on the page; entries expire after SECTION_CACHE_TTL_MS.
 * @type {Map<string, { text: string, expires: number }>}
 */
const SECTION_HTML_CACHE = new Map();

/** How long a cached section stays fresh (ms). Long enough to avoid re-fetch bursts, short enough to pick up inventory/price changes on a long-lived tab. */
const SECTION_CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Reads a fresh cache entry, evicting it if expired.
 * @param {string} url
 * @returns {string | undefined}
 */
function readSectionCache(url) {
  const entry = SECTION_HTML_CACHE.get(url);
  if (!entry) return undefined;
  if (Date.now() > entry.expires) {
    SECTION_HTML_CACHE.delete(url);
    return undefined;
  }
  return entry.text;
}

/**
 * @param {string} url
 * @param {string} text
 */
function writeSectionCache(url, text) {
  SECTION_HTML_CACHE.set(url, { text, expires: Date.now() + SECTION_CACHE_TTL_MS });
}

/**
 * Remembers, for a request URL that renders a combination nobody can buy, the URL that renders the
 * repaired one (see {@link VariantPicker.planOptionRepair}). Switching back and forth between prints
 * then costs a single render instead of "dead end, re-render" every time. Same TTL as the section
 * cache, because the repair picks the *first available* value and availability moves.
 * @type {Map<string, { url: string, expires: number }>}
 */
const REPAIR_REDIRECTS = new Map();

/**
 * @param {string} url
 * @returns {string | undefined}
 */
function readRepairRedirect(url) {
  const entry = REPAIR_REDIRECTS.get(url);
  if (!entry) return undefined;
  if (Date.now() > entry.expires) {
    REPAIR_REDIRECTS.delete(url);
    return undefined;
  }
  return entry.url;
}

/**
 * @param {string} url
 * @param {string} repairedUrl
 */
function writeRepairRedirect(url, repairedUrl) {
  REPAIR_REDIRECTS.set(url, { url: repairedUrl, expires: Date.now() + SECTION_CACHE_TTL_MS });
}

/**
 * Cheap dead-end test for a section we have not parsed: the picker serialises
 * `selected_or_first_available_variant`, which is `null` when the requested option combination
 * resolves to no variant at all. Used to keep the idle prefetch off the `DOMParser` for the common
 * (healthy) case — a false positive only costs one parse, since the planner re-checks properly.
 */
const NULL_VARIANT_JSON = /<script type="application\/json">\s*null\s*<\/script>/;

/** Max sibling prints to prefetch on page load; above this we skip (bandwidth guard). */
const MAX_PREFETCH_SIBLINGS = 12;

/** Shopify's Section Rendering API returns at most 5 sections per request (6+ → HTTP 400). */
const MAX_SECTIONS_PER_REQUEST = 5;

/**
 * Prefetches run through a small concurrency-limited queue so warming siblings never floods the
 * origin — `shopify theme dev` proxies every section render to Shopify and returns 429 under load.
 */
const PREFETCH_CONCURRENCY = 2;
/** URLs currently queued or in flight, so the same sibling is never prefetched twice. */
const PREFETCH_INFLIGHT = new Set();
/** @type {Array<() => Promise<unknown>>} */
const PREFETCH_QUEUE = [];
let prefetchActiveCount = 0;

function drainPrefetchQueue() {
  while (prefetchActiveCount < PREFETCH_CONCURRENCY && PREFETCH_QUEUE.length) {
    const task = /** @type {() => Promise<unknown>} */ (PREFETCH_QUEUE.shift());
    prefetchActiveCount++;
    Promise.resolve(task()).finally(() => {
      prefetchActiveCount--;
      drainPrefetchQueue();
    });
  }
}

/**
 * Enqueues a prefetch task keyed by URL (deduped), draining at most PREFETCH_CONCURRENCY at a time.
 * @param {string} key
 * @param {() => Promise<unknown>} task
 */
function enqueuePrefetch(key, task) {
  if (PREFETCH_INFLIGHT.has(key)) return;
  PREFETCH_INFLIGHT.add(key);
  PREFETCH_QUEUE.push(() => Promise.resolve(task()).finally(() => PREFETCH_INFLIGHT.delete(key)));
  drainPrefetchQueue();
}

/** requestIdleCallback with a setTimeout fallback for browsers that lack it. */
const requestIdle =
  typeof window !== 'undefined' && 'requestIdleCallback' in window
    ? window.requestIdleCallback.bind(window)
    : /** @param {() => void} cb */ (cb) => setTimeout(cb, 400);

/**
 * Deep-copies a node parsed out of a fetched section before handing it to {@link morph}.
 *
 * `morph` *adopts* nodes out of the new tree when the old tree is missing them (`appendChild` /
 * `insertBefore` move rather than copy), so morphing straight from the parsed document guts it.
 * That document is then dispatched on `VariantUpdateEvent.detail.data.html`, where product-price,
 * product-form, media-gallery, sticky-add-to-cart and friends read their own slice of it — and they
 * would read the hollowed-out leftovers and write them over the DOM the morph just got right.
 *
 * Concretely: switching to a combined-listing sibling whose price differs (including a sold-out
 * print, which renders an empty price) let `morph` move the new price text node into the page, then
 * product-price.js replaced that correct price with the now-empty node it found in `html` — so the
 * price vanished and never came back on subsequent switches.
 *
 * Morphing from a throwaway copy leaves the parsed document intact for every later consumer.
 *
 * @param {Element} node
 * @returns {Element}
 */
function cloneForMorph(node) {
  return /** @type {Element} */ (node.cloneNode(true));
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /**
   * Separate controller for the background fetch of ancillary sections (recommendations, reviews,
   * compare, TrustScore) so it neither aborts nor is aborted by the primary product-information swap.
   * @type {AbortController | undefined}
   */
  #ancillaryAbortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {boolean} Guards against scheduling more than one idle prefetch pass at a time. */
  #prefetchScheduled = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} Debounce timer for hover/focus prefetch. */
  #hoverIntentTimer;

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);

    // Warm the cache with each sibling print's section during idle time so switching is instant.
    this.#schedulePrefetch();

    // For listings too large to prefetch eagerly, warm a print on hover/focus (shopper intent).
    this.addEventListener('pointerover', this.#onOptionIntent, { passive: true });
    this.addEventListener('focusin', this.#onOptionIntent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Sets legend "selected value" text immediately for swatch fieldsets so it does not lag behind fetch+morph.
   * @param {HTMLElement} target - Change event target (radio input or select).
   */
  #syncSwatchLegendFromTarget(target) {
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.checked) return;

    const fieldset = target.closest('fieldset.variant-option--swatches');
    if (!fieldset) return;

    const legendValue = fieldset.querySelector(':scope > legend .variant-option__swatch-value');
    if (legendValue instanceof HTMLElement) {
      legendValue.textContent = target.value;
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#syncSwatchLegendFromTarget(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    // Single-variant combined-listing children come back as "/products/handle?variant=123".
    // Keep the path and the variant apart: assigning a string containing "?" to url.pathname
    // percent-encodes the "?" to %3F and breaks the link.
    const newUrlPath = newUrl ? newUrl.split('?')[0] : newUrl;
    const newUrlVariant = newUrl?.includes('?') ? new URLSearchParams(newUrl.split('?')[1]).get('variant') : null;
    const loadsNewProduct = isOnProductPage && !!newUrlPath && newUrlPath !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    // Render ONLY the product-information section for BOTH kinds of switch on the PDP.
    //
    // That section holds everything a variant change can touch — gallery, price, picker, buy box,
    // sticky bar, inventory, SKU, quantity rules, volume pricing — while the sections below it
    // (recommendations, reviews, compare, TrustScore) are product-scoped and were never applied
    // from this response anyway. Rendering them only to throw them away cost a multiple of the wire
    // size and of the main-thread `DOMParser` cost on every click.
    //
    // A combined-listing switch additionally *morphs* `.product-information` (below); a same-product
    // switch keeps morphing just the picker and letting the VariantUpdateEvent consumers read their
    // own slice out of `html`, exactly as before — they all live inside this section. Scoping also
    // removes an ambiguity: the unscoped `querySelector` calls those consumers make (`volume-pricing`,
    // `.quantity-rules`, `price-per-item`, …) used to run against a document that also contained the
    // recommendation carousels' own pickers, prices and product forms.
    //
    // Falls back to the previous full-page fetch / full-<main> morph if the section id can't be
    // resolved.
    const productInfoSectionId = isOnProductPage ? this.#productInformationSectionId() : null;

    const morphElementSelector = loadsNewProduct
      ? productInfoSectionId
        ? '.product-information'
        : 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Only the PDP can repair a dead-end combination: it needs a section id to re-render with, and
    // the option groups have to be the ones the shopper is looking at (not a card's swatch row).
    const repairProductPath = loadsNewProduct ? newUrlPath : this.dataset.productUrl?.split('?')[0];
    const repair =
      isOnProductPage && productInfoSectionId && repairProductPath
        ? {
            changedIndex: this.#changedOptionIndex(event.target),
            productPath: repairProductPath,
            sectionId: productInfoSectionId,
          }
        : undefined;

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption, null, [], productInfoSectionId),
      morphElementSelector,
      repair
    );

    // Best of both worlds: the section-scoped swap above updates the product-information instantly,
    // then we post-fetch the ancillary sections (recommendations, reviews, compare, TrustScore) for
    // the chosen print in the background and morph them in when ready. Only runs when we actually
    // section-scoped (otherwise the full-<main> fallback already refreshed everything).
    if (loadsNewProduct && productInfoSectionId && newUrlPath) {
      this.#postFetchAncillarySections(newUrlPath, this.selectedOptionsValues, productInfoSectionId);
    }

    // Changing a non-combining option (e.g. Size) changes the option_values the sibling URLs will
    // carry, so re-warm the prefetch cache for the new selection.
    if (!loadsNewProduct) this.#schedulePrefetch();

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || newUrlVariant || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrlPath;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string | null} [sectionId] - When set, request only this section (Section Rendering API) instead of the full page.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionId = null) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    // connectedProductUrl may carry a "?variant=…" for single-variant combined-listing children;
    // strip any query so appending our own params below can't produce a malformed double "?".
    if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (sectionId) {
      return `${productUrl}?section_id=${sectionId}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Finds this picker's counterpart in a freshly fetched document.
   * @param {Document | Element} html
   * @returns {Element | null}
   */
  #newPickerElement(html) {
    const tag = this.tagName.toLowerCase();
    const productId = this.dataset.productId;

    if ((tag === 'swatches-variant-picker-component' || tag === 'variant-picker') && productId) {
      // Combined listings load a sibling product with a *different* id, so scoping by the
      // current (stale) product id misses and would abort the update. Fall back to the
      // main-content picker (the freshly-loaded product) in that case.
      return (
        html.querySelector(`${tag}[data-product-id="${productId}"]`) ??
        html.querySelector(`main ${tag}`) ??
        html.querySelector(tag)
      );
    }

    return html.querySelector('variant-picker');
  }

  /**
   * @param {Document | Element} html
   * @returns {string | null}
   */
  #variantJsonScriptText(html) {
    return this.#newPickerElement(html)?.querySelector('script[type="application/json"]')?.textContent ?? null;
  }

  /**
   * The picker's option groups, in the order their values are serialised into `option_values`
   * (see {@link selectedOptionsValues}). Single-value options render as static text with no control
   * and contribute nothing, so they are not groups.
   * @param {Element} picker
   * @returns {Element[]}
   */
  #optionGroups(picker) {
    return Array.from(picker.querySelectorAll('fieldset[data-fieldset-index], .variant-option--dropdowns'));
  }

  /**
   * Reads what each option group has selected and what else it could select, from either the live
   * picker or a parsed one. Attributes rather than `:checked` / `.selected`, so a document that was
   * never inserted into the page reads the same as one that was.
   * @param {Element} picker
   * @returns {Array<{ selectedId: string | undefined, selectedAvailable: boolean, firstAvailableId: string | undefined }>}
   */
  #readOptionState(picker) {
    return this.#optionGroups(picker).map((group) => {
      const controls = /** @type {HTMLElement[]} */ (
        Array.from(group.querySelectorAll('input[data-option-value-id], option[data-option-value-id]'))
      );
      const selected = controls.find(
        (control) => control.dataset.currentChecked === 'true' || control.hasAttribute('selected')
      );
      const firstAvailable = controls.find((control) => control.dataset.optionAvailable !== 'false');

      return {
        selectedId: selected?.dataset.optionValueId,
        selectedAvailable: selected != null && selected.dataset.optionAvailable !== 'false',
        firstAvailableId: firstAvailable?.dataset.optionValueId,
      };
    });
  }

  /**
   * Position of the option group the shopper just interacted with, or -1 if it can't be placed.
   * @param {Element} control - The changed radio or select.
   * @returns {number}
   */
  #changedOptionIndex(control) {
    const group = control.closest('fieldset[data-fieldset-index], .variant-option--dropdowns');
    if (!group) return -1;
    return this.#optionGroups(this).indexOf(group);
  }

  /**
   * Decides whether a freshly rendered combination is a dead end and, if so, what to render instead.
   *
   * Carrying every selected option value across a switch is what makes "keep my size, show me the
   * other print" work — but on a combined listing the lower-priority options are the *sibling's*,
   * so the carried value routinely names something that print does not come in. Shopify then renders
   * a combination with no variant behind it: no price, an "Unavailable" buy button, and a shopper who
   * has to go and click the second option themselves. (Trimming the carried values does not help;
   * an option left unspecified falls back to its first value, available or not.)
   *
   * So: keep the option the shopper actually clicked and everything above it — that is the choice,
   * and a deliberate pick of a sold-out value must stay sold out — and re-point anything *below* it
   * that the new context cannot serve at that option's first available value.
   *
   * @param {Document | Element} html - The document just fetched.
   * @param {number} changedIndex - Position of the option group the shopper changed.
   * @param {string} productPath - Path of the product the document renders (no query string).
   * @param {string} sectionId - Section id to re-request.
   * @returns {string | null} A request URL for the repaired combination, or null if nothing to fix.
   */
  planOptionRepair(html, changedIndex, productPath, sectionId) {
    if (changedIndex < 0 || !productPath || !sectionId) return null;

    const picker = this.#newPickerElement(html);
    if (!picker) return null;

    const state = this.#readOptionState(picker);
    if (!state.length) return null;

    const variantJson = picker.querySelector('script[type="application/json"]')?.textContent?.trim();
    const hasNoVariant = !variantJson || variantJson === 'null';
    const hasUnbuyableSubOption = state.some((option, index) => index > changedIndex && !option.selectedAvailable);

    if (!hasNoVariant && !hasUnbuyableSubOption) return null;

    let repaired = false;
    /** @type {string[]} */
    const values = [];

    state.forEach((option, index) => {
      if (!option.selectedId) return;

      if (index <= changedIndex || option.selectedAvailable) {
        values.push(option.selectedId);
        return;
      }

      if (!option.firstAvailableId || option.firstAvailableId === option.selectedId) {
        values.push(option.selectedId);
        return;
      }

      values.push(option.firstAvailableId);
      repaired = true;
    });

    // Nothing swappable — the whole print really is sold out. Render what we fetched and let the buy
    // box say so, rather than spending a second request to arrive at the same page.
    if (!repaired) return null;

    return this.#composeSectionUrl(productPath, values, sectionId);
  }

  /**
   * @typedef {object} RepairOptions
   * @property {number} changedIndex - Position of the option group the shopper changed.
   * @property {string} productPath - Path of the product being rendered (no query string).
   * @property {string} sectionId - Section id to re-request with.
   * @property {boolean} [isRepair] - Set on the follow-up request, so a repair can't repair itself.
   */

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {RepairOptions} [repair] - When set, an unbuyable result is re-rendered with the lower-priority options moved to available values.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, repair) {
    // A switch we have already repaired once goes straight to the combination that worked, so
    // toggling back and forth doesn't pay for the dead-end render every time.
    if (repair && !repair.isRepair) {
      const knownGood = readRepairRedirect(requestUrl);
      if (knownGood) {
        this.fetchUpdatedSection(knownGood, morphElementSelector, { ...repair, isRepair: true });
        return;
      }
    }

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Only the combined-listing sibling switch (product-information section) uses the prefetch cache,
    // keeping cache/staleness scoped to the path we're optimizing. Serve from cache when warm,
    // otherwise fetch and store for next time. Same-product switches are section-scoped too but stay
    // uncached on purpose, so each click reads live price and inventory for the chosen variant.
    const cacheable = morphElementSelector === '.product-information';
    const cached = cacheable ? readSectionCache(requestUrl) : undefined;
    const responseText$ =
      cached != null
        ? Promise.resolve(cached)
        : fetch(requestUrl, { signal: this.#abortController.signal })
            .then((response) => response.text())
            .then((text) => {
              if (cacheable) writeSectionCache(requestUrl, text);
              return text;
            });

    responseText$
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Never show the shopper a combination that carried a value the new print doesn't come in —
        // re-render with that option moved to something buyable and apply *that* instead.
        if (repair && !repair.isRepair) {
          const repairUrl = this.planOptionRepair(html, repair.changedIndex, repair.productPath, repair.sectionId);

          if (repairUrl) {
            writeRepairRedirect(requestUrl, repairUrl);
            this.fetchUpdatedSection(repairUrl, morphElementSelector, { ...repair, isRepair: true });
            return;
          }
        }

        const textContent = this.#variantJsonScriptText(html);
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          // The morph rewrites this picker's own data attributes along with everything else in
          // the subtree, so "did the product change" is the before/after of our product id.
          // Consumers outside the morphed subtree (sticky-add-to-cart sits beside
          // `.product-information`, not inside it) rely on `newProduct` to adopt the sibling.
          const previousProductId = this.dataset.productId;
          this.updateElement(html, morphElementSelector);
          const { productId, productUrl } = this.dataset;
          if (productId && productUrl && productId !== previousProductId) {
            newProduct = { id: productId, url: productUrl };
          }
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Before anything reads the picker back (`selectedOptionId` below included), make sure it
        // actually shows what was rendered.
        this.#reconcileSelection(html);

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }

        // `variantChanged` wrote `?variant=` from the value the shopper clicked, before we knew a
        // repair would land on a different variant. Bring the address bar back in line with what is
        // now on screen, so a reload or a shared link reproduces this page.
        if (repair?.isRepair) this.#syncVariantParam(textContent);

        // The morph may have swapped in a different product (combined-listing sibling); warm the
        // cache for the new product's siblings so the next switch is instant too.
        this.#schedulePrefetch();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Makes the live picker agree with the section that was just applied, for any option group the
   * *server* pointed somewhere else than the shopper did — a repaired sub-option, or an option a
   * combined-listing sibling resolves differently.
   *
   * The morph cannot be relied on for this. It deliberately preserves `data-current-checked` /
   * `data-previous-checked` (those are client-owned; they drive the pill animation), and its boolean
   * attribute update writes `checked=""` even when *un*checking — which, combined with radio `name`
   * attributes that carry the product id and so change mid-walk on a sibling switch, can leave a
   * group flagged on the old value or with nothing checked at all.
   *
   * Groups the shopper's own click already settled are left alone, so the pill still animates from
   * the value they came from.
   *
   * @param {Document | Element} html - The document that was just applied.
   */
  #reconcileSelection(html) {
    const newPicker = this.#newPickerElement(html);
    if (!newPicker) return;

    const renderedGroups = this.#optionGroups(newPicker);
    const liveGroups = this.#optionGroups(this);
    // A different option count means the morph reshaped the picker beyond what we can map by
    // position; the rendered markup is authoritative there and there is nothing safe to reconcile.
    if (!renderedGroups.length || renderedGroups.length !== liveGroups.length) return;

    renderedGroups.forEach((renderedGroup, index) => {
      const liveGroup = liveGroups[index];
      if (!liveGroup) return;

      const rendered = /** @type {HTMLElement | null} */ (
        renderedGroup.querySelector('input[data-current-checked="true"], option[selected]')
      );
      const optionValueId = rendered?.dataset.optionValueId;
      if (!optionValueId) return;

      const live = liveGroup.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);

      if (live instanceof HTMLInputElement) {
        if (live.checked && live.dataset.currentChecked === 'true') return;
        this.updateSelectedOption(live);
        this.#syncSwatchLegendFromTarget(live);
        return;
      }

      if (live instanceof HTMLOptionElement) {
        const select = live.closest('select');
        if (!(select instanceof HTMLSelectElement) || select.value === live.value) return;
        select.value = live.value;
        this.updateSelectedOption(select);
      }
    });
  }

  /**
   * Points `?variant=` at the variant a rendered section actually resolved to. No-op off the product
   * page (cards and quick-add must not touch the address bar).
   * @param {string} variantJsonText - The picker's serialised `selected_or_first_available_variant`.
   */
  #syncVariantParam(variantJsonText) {
    if (this.dataset.templateProductMatch !== 'true') return;

    let variantId;
    try {
      variantId = JSON.parse(variantJsonText)?.id;
    } catch {
      return;
    }
    if (!variantId) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('variant') === String(variantId)) return;

    url.searchParams.set('variant', String(variantId));
    history.replaceState({}, '', url.toString());
  }

  /**
   * Resolves the section id of the enclosing `product-information` section, or null if this picker
   * isn't inside one (e.g. product cards, quick-add). Used to request a section-scoped render.
   * @returns {string | null}
   */
  #productInformationSectionId() {
    // `_product-details` renders `id="ProductInformation-{{ section.id }}"` and is an ancestor of the
    // picker on the PDP — the most reliable source for the section id.
    const details = this.closest('[id^="ProductInformation-"]');
    if (details instanceof HTMLElement && details.id) {
      return details.id.replace(/^ProductInformation-/, '');
    }
    // Fallback: the platform section wrapper.
    const wrapper = this.closest('.shopify-section');
    if (wrapper instanceof HTMLElement && wrapper.id && wrapper.querySelector('.product-information')) {
      return wrapper.id.replace(/^shopify-section-/, '');
    }
    return null;
  }

  /**
   * Schedules an idle pass that prefetches every sibling print's product-information section for the
   * current selection and stores it in {@link SECTION_HTML_CACHE}. No-op off the product page, when
   * the section id can't be resolved, on Save-Data connections, or when there are too many siblings.
   */
  #schedulePrefetch() {
    if (this.#prefetchScheduled) return;
    if (this.dataset.templateProductMatch !== 'true') return;
    // @ts-ignore - navigator.connection is not in every lib.dom
    if (navigator.connection?.saveData) return;

    const sectionId = this.#productInformationSectionId();
    if (!sectionId) return;

    this.#prefetchScheduled = true;
    requestIdle(() => {
      this.#prefetchScheduled = false;
      this.#prefetchSiblings(sectionId);
    });
  }

  /**
   * Prefetches the product-information section of each sibling print (carrying the currently selected
   * non-combining options, e.g. Size) and caches the HTML. Failures are swallowed — a miss simply
   * falls back to a live (still section-scoped) fetch.
   * @param {string} sectionId
   */
  #prefetchSiblings(sectionId) {
    const siblingRadios = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('input[type="radio"][data-connected-product-url]'))
    );
    // Small listings are prefetched eagerly on load; larger ones rely on hover/focus prefetch instead.
    if (!siblingRadios.length || siblingRadios.length > MAX_PREFETCH_SIBLINGS) return;

    for (const radio of siblingRadios) this.#prefetchSibling(radio, sectionId);
  }

  /**
   * Prefetches and caches a single sibling print's product-information section for the current
   * selection. No-op if it's the current product or already cached.
   * @param {HTMLInputElement} radio
   * @param {string} sectionId
   */
  #prefetchSibling(radio, sectionId) {
    const connected = radio.dataset.connectedProductUrl;
    if (!connected) return;
    const path = connected.split('?')[0];
    if (!path || path === this.dataset.productUrl?.split('?')[0]) return;

    const url = this.#composeSectionUrl(path, this.#optionValuesForSibling(radio), sectionId);

    this.#warmSection(url, (text) => this.#prefetchRepair(url, text, radio, sectionId));
  }

  /**
   * Fetches a section URL into {@link SECTION_HTML_CACHE} unless it is already warm.
   * @param {string} url
   * @param {(text: string) => void} [onText] - Runs on a freshly fetched (not cached) section.
   */
  #warmSection(url, onText) {
    if (readSectionCache(url)) return;

    // Route through the bounded queue so a burst of prefetches (large listing / hover sweep) can't
    // flood the origin and 429.
    enqueuePrefetch(url, () =>
      // @ts-ignore - `priority` is a valid RequestInit hint where supported, ignored elsewhere.
      fetch(url, { priority: 'low' })
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          if (text == null) return;
          writeSectionCache(url, text);
          // Warm the sibling's primary gallery image too — the section HTML swaps in instantly from
          // cache, but the new print's photos would otherwise still load over the network on click.
          this.#preloadPrimaryImage(text);
          onText?.(text);
        })
        .catch(() => {})
    );
  }

  /**
   * A prefetched sibling that renders an unbuyable combination costs the shopper a second round trip
   * on click (see {@link planOptionRepair}). Spot it while idle, work out the repair, and warm that
   * too — so the switch stays instant *and* lands on something they can buy.
   * @param {string} url - The URL the click on `radio` will request.
   * @param {string} sectionHtml - The prefetched section.
   * @param {HTMLInputElement} radio
   * @param {string} sectionId
   */
  #prefetchRepair(url, sectionHtml, radio, sectionId) {
    // Cheap string test first; parsing every warmed sibling would put the idle pass on the main
    // thread for no reason, since almost all of them render fine.
    if (!NULL_VARIANT_JSON.test(sectionHtml)) return;
    if (readRepairRedirect(url)) return;

    const path = radio.dataset.connectedProductUrl?.split('?')[0];
    if (!path) return;

    const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const repairUrl = this.planOptionRepair(html, this.#changedOptionIndex(radio), path, sectionId);
    if (!repairUrl) return;

    writeRepairRedirect(url, repairUrl);
    this.#warmSection(repairUrl);
  }

  /**
   * Preloads the first gallery image found in a prefetched section so the media swap is instant.
   *
   * The warm-up `Image` mirrors the gallery `<img>`'s `srcset` + `sizes`, not just its `src`. The
   * `src` is the 3840px fallback, which the real element never requests — it resolves a candidate
   * from `srcset` by `sizes` × DPR (typically 832–1600px). Warming `src` alone therefore cost a 4K
   * download per sibling *and* still missed cache on click. Mirroring the attributes makes the
   * browser resolve — and cache — the exact URL the morphed-in gallery will ask for.
   * @param {string} sectionHtml
   */
  #preloadPrimaryImage(sectionHtml) {
    const tag = sectionHtml.match(/<media-gallery[\s\S]*?<img\b([^>]*)>/i)?.[1];
    if (!tag) return;
    /** @param {string} name */
    const attr = (name) => tag.match(new RegExp(`\\s${name}="([^"]*)"`, 'i'))?.[1]?.replace(/&amp;/g, '&');
    const src = attr('src');
    if (!src) return;

    const img = new Image();
    // @ts-ignore - fetchPriority is valid on HTMLImageElement where supported.
    img.fetchPriority = 'low';
    img.decoding = 'async';
    // `sizes` before `srcset`: candidate selection runs when `srcset`/`src` is assigned and reads
    // whatever `sizes` is at that moment.
    const sizes = attr('sizes');
    const srcset = attr('srcset');
    if (sizes) img.sizes = sizes;
    if (srcset) img.srcset = srcset;
    img.src = src;
  }

  /**
   * Warms a single sibling's section as soon as the shopper shows intent (hovers or focuses a print
   * swatch). This makes the click instant even for listings too large to prefetch eagerly on load.
   * @param {Event} event
   */
  #onOptionIntent = (event) => {
    if (this.dataset.templateProductMatch !== 'true') return;
    // @ts-ignore - navigator.connection is not in every lib.dom
    if (navigator.connection?.saveData) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const radio =
      target instanceof HTMLInputElement && target.dataset.connectedProductUrl
        ? target
        : target.closest('label')?.querySelector('input[data-connected-product-url]');
    if (!(radio instanceof HTMLInputElement)) return;

    // Debounce so sweeping the cursor across swatches only warms the one the shopper settles on.
    clearTimeout(this.#hoverIntentTimer);
    this.#hoverIntentTimer = setTimeout(() => {
      const sectionId = this.#productInformationSectionId();
      if (sectionId) this.#prefetchSibling(radio, sectionId);
    }, 120);
  };

  /**
   * Builds the option_values array a click on `radio` would send: the sibling's own value for its
   * fieldset, plus the current checked value of every other fieldset (in DOM order), mirroring
   * {@link selectedOptionsValues}.
   * @param {HTMLInputElement} radio
   * @returns {string[]}
   */
  #optionValuesForSibling(radio) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    /** @type {string[]} */
    const values = [];

    for (const fieldset of fieldsets) {
      const input = fieldset.contains(radio) ? radio : fieldset.querySelector('input:checked');
      const optionValueId = input instanceof HTMLInputElement ? input.dataset.optionValueId : undefined;
      if (optionValueId) values.push(optionValueId);
    }

    return values;
  }

  /**
   * Composes a section-scoped request URL identical to what {@link buildRequestUrl} produces for a
   * sibling navigation, so a prefetched entry is a cache hit for the real click.
   * @param {string} path - Sibling product path (no query string).
   * @param {string[]} optionValueIds
   * @param {string} sectionId
   * @returns {string}
   */
  #composeSectionUrl(path, optionValueIds, sectionId) {
    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    if (optionValueIds.length) params.push(`option_values=${optionValueIds.join(',')}`);
    return `${path}?section_id=${sectionId}&${params.join('&')}`;
  }

  /**
   * After the instant product-information swap, fetch every *other* section in <main> for the chosen
   * sibling (recommendations, reviews, compare, TrustScore, etc.) in one Section Rendering API call
   * and morph each into place. Runs in the background, is aborted if the shopper switches again, and
   * fails silently (a failure just leaves the previous print's ancillary content in place).
   * @param {string} siblingPath - The sibling product path (no query string).
   * @param {string[]} optionValueIds - The selected option value ids to render with.
   * @param {string} productInfoSectionId - The product-information section id (excluded from the fetch).
   */
  #postFetchAncillarySections(siblingPath, optionValueIds, productInfoSectionId) {
    const main = document.querySelector('main');
    if (!main) return;

    const ancillaryIds = Array.from(main.querySelectorAll('[id^="shopify-section-"]'))
      .map((el) => el.id.replace(/^shopify-section-/, ''))
      .filter((id) => id && id !== productInfoSectionId);
    if (!ancillaryIds.length) return;

    this.#ancillaryAbortController?.abort();
    this.#ancillaryAbortController = new AbortController();
    const { signal } = this.#ancillaryAbortController;

    const path = siblingPath.split('?')[0];
    const extraParams = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) extraParams.push(`view=${viewParamValue}`);
    if (optionValueIds.length) extraParams.push(`option_values=${optionValueIds.join(',')}`);
    const extra = extraParams.length ? `&${extraParams.join('&')}` : '';

    // Apply a `?sections=` JSON payload (map of section-id → HTML) into the live DOM.
    /** @param {Record<string, unknown> | null | undefined} sections */
    const applySections = (sections) => {
      if (!sections) return;
      for (const [id, value] of Object.entries(sections)) {
        if (typeof value !== 'string') continue;
        const target = document.getElementById(`shopify-section-${id}`);
        if (!target) continue;

        const parsed = new DOMParser().parseFromString(value, 'text/html');
        // `?sections=` returns each section including its `#shopify-section-<id>` wrapper; fall
        // back to replacing inner HTML if only the inner content is returned.
        const replacement = parsed.getElementById(`shopify-section-${id}`);
        if (replacement) {
          morph(target, replacement);
        } else {
          const clone = /** @type {HTMLElement} */ (target.cloneNode(false));
          clone.innerHTML = value;
          morph(target, clone);
        }

        // Morph swaps markup only: a `<script>` parsed out of the response is inert, so anything the
        // section bootstrapped on page load (quote clamps, Judge.me's widget) is now detached from the
        // DOM it was bootstrapped on. Let the section re-initialise itself.
        target.dispatchEvent(new SectionUpdateEvent(id, target));
      }
    };

    // The Section Rendering API caps how many sections one request may return, so fetch in chunks.
    // Each chunk is independent — a failing chunk just leaves those sections on the previous print.
    for (let i = 0; i < ancillaryIds.length; i += MAX_SECTIONS_PER_REQUEST) {
      const chunk = ancillaryIds.slice(i, i + MAX_SECTIONS_PER_REQUEST);
      const chunkUrl = `${path}?sections=${chunk.join(',')}${extra}`;

      // Cache key excludes the volatile `option_values`/`view` params on purpose: these ancillary
      // sections (recommendations, reviews, brand, offer ribbon) are per-product, and the sibling
      // path already identifies the product. Keying on the full URL would miss on re-visit because
      // a combined-listing sibling reached from different prints serializes different option ids.
      const cacheKey = `${path}?sections=${chunk.join(',')}`;

      // Serve from the TTL cache when warm — a variant toggled back to shouldn't re-hit the origin.
      const cachedChunk = readSectionCache(cacheKey);
      if (cachedChunk) {
        try {
          applySections(JSON.parse(cachedChunk));
        } catch (error) {
          console.error(error);
        }
        continue;
      }

      // @ts-ignore - `priority` is a valid RequestInit hint where supported, ignored elsewhere.
      fetch(chunkUrl, { signal, priority: 'low' })
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          if (text == null) return;
          writeSectionCache(cacheKey, text);
          applySections(JSON.parse(text));
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.error(error);
        });
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const tag = this.tagName.toLowerCase();
    const productId = this.dataset.productId;
    const scopedSelector =
      productId && (tag === 'swatches-variant-picker-component' || tag === 'variant-picker')
        ? `${tag}[data-product-id="${productId}"]`
        : tag;
    const newVariantPickerSource = newHtml.querySelector(scopedSelector);

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, cloneForMorph(newElement));
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, cloneForMorph(newMain));
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
